import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Category } from 'src/redux/apiTypes';
import { useAppConfig } from 'src/utils/appConfig/useAppConfig';
import { sendCategoryPage } from 'src/utils/marketing/enhancedEcommerce';
import { useAppSelector } from '../../../redux/hooks';
import getAbsoluteUrl from '../../../utils/api/getAbsoluteUrl';
import { defineCategoriesRobotsAndCanonicalMode } from '../../../utils/marketing/defineRobotsAndCanonicalMode/defineCategoriesRobotsAndCanonicalMode';
import getSeoPagination from '../../../utils/marketing/getSeoPagination';
import { getCanonicalHref } from '../../containers/RobotsAndCanonicalModeManager/RobotsAndCanonicalModeManager';

export interface CategoriesHeadProps {
  count?: number;
  page?: number;
  selectedCategory: Category;
  filter: string;
  title: string;
  description: string;
}

const CategoriesHead = (props: CategoriesHeadProps) => {
  const { selectedCategory, count, description, title } = props;
  const router = useRouter();
  const { host } = useAppConfig();
  const withGetParam = router.asPath.includes('/?');
  const categoryLevelIsSecondOrHigher = useAppSelector(s => !!Object.values(s.catalogue.categoryData || {}).length);

  const categoryTitle = selectedCategory.title;
  const withAdvertisementParamsOnly = useAppSelector(s => {
    const categoryFilters = s.catalogue?.categoryData?.filters || [];
    const filterKeys = categoryFilters.map(item => item.key);
    return filterKeys.some(item => router.asPath.includes(`?${item}=`) || router.asPath.includes(`&${item}=`));
  });

  const {
    withCanonicalTag,
    indexAndFollow,
    noIndexAndFollow,
    noIndexNoFollow,
  } = defineCategoriesRobotsAndCanonicalMode({
    urlStr: router.asPath,
    router,
    categoryLevelIsSecondOrHigher,
    withAdvertisementParamsOnly,
  });

  useEffect(() => {
    sendCategoryPage(categoryTitle);
  }, [categoryTitle]);


  const countItem = count || 1;

  return (
    <Head data-testid='head'>
      {withCanonicalTag &&
        <link
          rel="canonical"
          href={getCanonicalHref(getAbsoluteUrl(router.asPath, host).fullUrl, withGetParam)}
        />
      }
      {
        getSeoPagination({ router, countItem, host })
      }
      {indexAndFollow &&
        <meta name="robots" content="index, follow" />
      }
      {noIndexAndFollow &&
        <meta name="robots" content="noindex, follow" />
      }
      {(noIndexNoFollow) &&
        <meta name="robots" content="noindex, nofollow" />
      }
      <title key='title'>
        {title}
      </title>
      <meta name='description' content={description} key='meta-description' />
    </Head>
  );
};

export default CategoriesHead;
